import { Component, forwardRef, Input, OnInit } from '@angular/core';
import {
    ControlValueAccessor,
    FormBuilder,
    FormControl,
    FormGroup,
    FormsModule,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-vod-repackaging-format-specific',
    templateUrl: './vod-repackaging-format-specific.component.html',
    styleUrls: ['./vod-repackaging-format-specific.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => VodRepackagingFormatSpecificComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: VodRepackagingFormatSpecificComponent,
            multi: true
        }
    ],
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule
    ]
})
export class VodRepackagingFormatSpecificComponent implements OnInit, ControlValueAccessor {
    static DEFAULTS = {
        encrypt: true,
        onboard: false,
        onboard_tags: [],
        onboard_window_start: null,
        onboard_window_end: null,
    };

    @Input()
    parentForm: FormGroup<any>;

    form: FormGroup;

    protected readonly environment = environment;

    changed: any = () => {
    };
    touched: any = () => {
    };

    constructor(private formBuilder: FormBuilder) {
        this.form = this.formBuilder.group({
            encrypt: true,
            onboard: false,
            onboard_tags: [[]],
            onboard_window_start: [null],
            onboard_window_end: [null]
        })
        this.form.valueChanges.subscribe((val) => {
            this.touched();
            // Transform form values to backend format
            const transformedVal = { ...val };

            // Transform onboard fields if encryption is enabled AND onboard is enabled
            if (transformedVal.encrypt && transformedVal.onboard) {
                // Build onboard_window object
                if (transformedVal.onboard_window_start && transformedVal.onboard_window_end) {
                    transformedVal.onboard_window = {
                        start_time: this.formatDateTimeForBackend(transformedVal.onboard_window_start),
                        end_time: this.formatDateTimeForBackend(transformedVal.onboard_window_end)
                    };
                }
                // Transform comma-separated tags to array
                if (typeof transformedVal.onboard_tags === 'string') {
                    transformedVal.onboard_tags = transformedVal.onboard_tags
                        .split(',')
                        .map(tag => tag.trim())
                        .filter(tag => tag !== '');
                } else if (Array.isArray(transformedVal.onboard_tags)) {
                    // Already an array, keep as is
                } else {
                    transformedVal.onboard_tags = [];
                }
            }

            // Remove form-specific fields that shouldn't be sent to backend
            delete transformedVal.onboard_window_start;
            delete transformedVal.onboard_window_end;

            this.changed(transformedVal);
        });
        this.form.get('onboard').valueChanges.subscribe((val) => {
            if (val) {
                this.form.controls['onboard_tags'].enable()
                this.form.controls['onboard_window_start'].enable()
                this.form.controls['onboard_window_end'].enable()
            } else {
                this.form.controls['onboard_tags'].disable()
                this.form.controls['onboard_window_start'].disable()
                this.form.controls['onboard_window_end'].disable()
            }
        })
        // When encryption is disabled, also disable all onboard fields
        this.form.get('encrypt').valueChanges.subscribe((val) => {
            if (!val) {
                this.form.controls['onboard'].disable()
                this.form.controls['onboard_tags'].disable()
                this.form.controls['onboard_window_start'].disable()
                this.form.controls['onboard_window_end'].disable()
            } else {
                this.form.controls['onboard'].enable()
                // Tags and window fields are controlled by onboard value
            }
        })
    }

    ngOnInit(): void {
    }

    writeValue(value: any): void {
        if (value) {
            // Transform onboard_window object to separate fields for the form
            if (value.onboard_window) {
                value.onboard_window_start = value.onboard_window.start_time;
                value.onboard_window_end = value.onboard_window.end_time;
                delete value.onboard_window;
            }
            // Transform onboard_tags array to comma-separated string
            if (value.onboard_tags && Array.isArray(value.onboard_tags)) {
                value.onboard_tags = value.onboard_tags.join(', ');
            }
            this.form.patchValue(value, { emitEvent: false });
        }
    }

    registerOnChange(fn: any): void {
        this.changed = fn;
    }

    registerOnTouched(fn: any): void {
        this.touched = fn;
    }

    setDisabledState ?(isDisabled: boolean): void {
        if (isDisabled) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    validate(control: FormControl) {
        const isNotValid = !this.form.valid
        return isNotValid && {
            invalid: true
        }
    }

    private formatDateTimeForBackend(dateTimeLocalValue: string): string {
        // datetime-local format is YYYY-MM-DDTHH:mm
        // Backend expects ISO 8601 with Z: YYYY-MM-DDTHH:mmZ
        if (!dateTimeLocalValue) return null;
        // Add seconds if not present and append Z for UTC
        return dateTimeLocalValue.includes(':') && dateTimeLocalValue.split(':').length === 2
            ? `${dateTimeLocalValue}:00Z`
            : `${dateTimeLocalValue}Z`;
    }
}



